import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { ArrowLeft } from "lucide-react";
import BASE_URL from "../Config/apiConfig";
import axios from "axios";

const Cartcomponents = () => {
  const [cart, setCart] = useState([]);
  const [address, setAddress] = useState(null);
  const [wishlist, setWishlist] = useState([]);

  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?._id;
  const wishlistKey = userId ? `wishlist_${userId}` : null;

  /* LOAD CART */
  useEffect(() => {
    if (userId) {
      const storedCart =
        JSON.parse(localStorage.getItem(`cart_${userId}`)) || [];
      setCart(storedCart);
    }
  }, [userId]);

  /* LOAD WISHLIST */
  useEffect(() => {
    if (!wishlistKey) return;
    const storedWishlist =
      JSON.parse(localStorage.getItem(wishlistKey)) || [];
    setWishlist(storedWishlist);
  }, [wishlistKey]);

  /* LOAD ADDRESS */
  useEffect(() => {
    const savedAddress = localStorage.getItem("selectedAddress");
    if (savedAddress) {
      setAddress(JSON.parse(savedAddress));
    }
  }, []);

  const updateCart = (newCart) => {
    setCart(newCart);
    if (userId) {
      localStorage.setItem(`cart_${userId}`, JSON.stringify(newCart));
    }
  };

  const increaseQty = (id) => {
    updateCart(
      cart.map((i) =>
        i._id === id ? { ...i, qty: i.qty + 1 } : i
      )
    );
  };

  const decreaseQty = (id) => {
    updateCart(
      cart.map((i) =>
        i._id === id
          ? { ...i, qty: i.qty > 1 ? i.qty - 1 : 1 }
          : i
      )
    );
  };

  const removeItem = (id) => {
    updateCart(cart.filter((i) => i._id !== id));
  };

  const toggleWishlist = (item) => {
    if (!wishlistKey) return toast.warning("Login first");

    const exists = wishlist.some((w) => w._id === item._id);

    const updated = exists
      ? wishlist.filter((w) => w._id !== item._id)
      : [...wishlist, item];

    setWishlist(updated);
    localStorage.setItem(wishlistKey, JSON.stringify(updated));
  };

  const isWishlisted = (id) =>
    wishlist.some((i) => i._id === id);

  /* CALCULATIONS */
  const totalMRP = cart.reduce(
    (sum, item) => sum + item.price * item.qty,
    0
  );

  const discount = totalMRP * 0.1;
  const shipping = totalMRP > 500 ? 0 : 50;
  const grandTotal = totalMRP - discount + shipping;

  const loadScript = (src) => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // 🔥 FINAL PLACE ORDER
  const placeOrder = async () => {
    if (!userId) {
      toast.warning("Please login first");
      return;
    }

    if (!address) {
      toast.info("Please select address");
      navigate("/edit-profile", {
        state: { section: "addressinfo" },
      });
      return;
    }

    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    const res = await loadScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!res) {
      alert("Razorpay SDK failed to load.");
      return;
    }

    const options = {
      key: "rzp_test_5q0fSlYlac5Ck0",
      amount: Math.round(grandTotal * 100),
      currency: "INR",
      name: "Medicine Drugstore",
      description: "Order Payment",

      handler: async function (response) {
        try {
          const orderData = {
            userId: userId,

            items: cart.map((item) => ({
              productId: item._id,
              name: item.pName,
              price: item.price,
              qty: item.qty,
              img: item.pImg?.[0],
            })),

            address: address,
            totalMRP,
            discount,
            shipping,
            grandTotal,

            // ✅ Razorpay payment id
            razorpayPaymentId: response.razorpay_payment_id,
            paymentStatus: "Paid",
          };

          const responseData = await axios.post(
            `http://localhost:8500/api/v1/create`,
            orderData
          );

          const awb = responseData.data.awb;

          toast.success("Payment Successful ✅");

          localStorage.removeItem(`cart_${userId}`);
          setCart([]);

          // 🔥 Navigate with AWB
          navigate(`/order/${responseData.data.order._id}`, {
            state: { awb },
          });

        } catch (error) {
          console.log(error);
          toast.error("Order creation failed");
        }
      },

      prefill: {
        name: address?.fullName,
        email: user?.email,
        contact: address?.mobileNumber,
      },

      theme: {
        color: "#0f766e",
      },
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      {/* HEADER */}
      <div className="bg-white border-b">
        <div
          className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="cursor-pointer text-gray-600" />
          <h1 className="text-lg font-semibold text-[#187685]">
            Shopping Cart
          </h1>
        </div>
      </div>

      {/* MAIN */}
      <div className="max-w-7xl mx-auto px-4 py-6 grid lg:grid-cols-12 gap-5">

        {/* LEFT */}
        <div className="lg:col-span-8 space-y-4">

          {/* ADDRESS */}
          <div className="bg-white border rounded-lg p-4 flex justify-between">
            <div>
              <p className="text-sm font-semibold">Deliver to</p>
              <p className="text-sm text-gray-600 mt-1">
                {address
                  ? `${address.flatHouse}, ${address.streetArea}, ${address.city}`
                  : "No address selected"}
              </p>
            </div>

            <button
              onClick={() =>
                navigate("/edit-profile", {
                  state: { section: "addressinfo" },
                })
              }
              className="text-[#187685] text-sm font-medium"
            >
              Change
            </button>
          </div>

          {/* CART ITEMS */}
          <div className="bg-white border rounded-lg">
            <div className="border-b px-4 py-3">
              <h3 className="font-semibold">
                Cart Items ({cart.length})
              </h3>
            </div>

            {cart.map((item) => (
              <div key={item._id} className="flex gap-4 p-4 border-b">
                <img
                  src={`https://mediapi.onlinepharmaque.in/uploads/${item.pImg?.[0]}`}
                  className="w-20 h-20 object-cover border rounded"
                  alt=""
                />

                <div className="flex-1">
                  <h4 className="font-medium">{item.pName}</h4>
                  <p className="text-sm text-gray-500">
                    {item.description}
                  </p>
                  <p className="text-lg font-semibold text-[#187685]">
                    ₹{item.price}
                  </p>

                  <div className="flex gap-4 mt-2">
                    <button
                      onClick={() => removeItem(item._id)}
                      className="text-red-500"
                    >
                      Remove
                    </button>

                    <button onClick={() => toggleWishlist(item)}>
                      {isWishlisted(item._id) ? (
                        <FaHeart className="text-red-500" />
                      ) : (
                        <FaRegHeart />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-center">
                  <button onClick={() => decreaseQty(item._id)}>-</button>
                  <span className="px-3">{item.qty}</span>
                  <button onClick={() => increaseQty(item._id)}>+</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT */}
        <div className="lg:col-span-4">
          <div className="bg-white border p-5 rounded-lg sticky top-5">

            <h3 className="font-semibold mb-4">Price Details</h3>

            <div className="flex justify-between">
              <span>Total</span>
              <span>₹{grandTotal}</span>
            </div>

            <button
              onClick={placeOrder}
              className="w-full mt-5 bg-[#187685] text-white py-3 rounded"
            >
              Place Order
            </button>

          </div>
        </div>

      </div>
    </div>
  );
};

export default Cartcomponents;